import React, { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { actions as dialogActions } from "@Store/Reducers/dialog";
import { Box, Button, Link } from "@mui/material";
import Lottie from "lottie-react";
import successfulVoted from "./animations/voteSuccess.json";
import registeredVote from "./animations/voteRegistered.json";
import { AuthSelectors, PollSelectors } from "@Store";
import { IChannelSettings, RootStateType, IChannel } from "@Interfaces";
import { DialogComponents } from "@Interfaces/Entities/Dialogs";
import { useNavigate } from "react-router-dom";
import { isGuestDataStored } from "@Utils/isGuestDataStored";
import { getChannelCoverUrl } from "@Utils/getChannelCoverUrl";
import { isDataCollectionRequired } from "@Utils/isDataCollectionRequired";

interface IVotedChoiceContent {
    choice: string; // Currently not used in rendering. Integrate or remove as needed.
}

const voteMessageStyle: React.CSSProperties = {
    margin: "0 0 5px 0",
    fontSize: "31px",
    textAlign: "center",
};

const VotedChoiceContent: React.FC<IVotedChoiceContent> = ({ choice }) => {
    const dispatch = useDispatch<any>();
    const navigate = useNavigate();
    // Extract poll-related state from Redux
    const user_choice = useSelector(
        (state: RootStateType) => state.poll?.["user_choice"]
    );
    const voteId: string | undefined = useSelector(
        (state: RootStateType) => state?.poll?.["vote_id"]
    );
    const settings = useSelector<RootStateType, IChannelSettings | undefined>(
        (state) => state?.poll?.["settings"]
    );
    const author: IChannelSettings | undefined = useSelector(
        (state: RootStateType) => state?.poll?.["author"]
    );
    const channels = useSelector<RootStateType, IChannel[]>(
        (state) => state.channels.channels ?? []
    );
    const pollChannelId = useSelector<RootStateType, string | undefined>(
        (state) => state?.poll?.["channel_id"]
    );

    const isAuthUser = AuthSelectors.selectIsUserAuthorized();
    const authUserVoted = isAuthUser && Boolean(user_choice);
    const guestUserVoted = PollSelectors.selectIsVoteRegistered();

    const dataCollectionRequired = isDataCollectionRequired(settings);
    const canViewResults = isAuthUser || !settings?.hide_results_before_voting;
    const ctaMsg =
        settings?.["cta_msg"] ||
        (dataCollectionRequired ? "Continue" : "Show Results");

    // Memoize the function to avoid unnecessary re-creations on re-renders.
    const dispatchGuestDialog = useCallback(() => {
        if (!isGuestDataStored() && voteId && dataCollectionRequired) {
            const channel = channels?.find((c) => c._id === pollChannelId);
            const coverUrl = getChannelCoverUrl(channel);
            dispatch(
                dialogActions.openDialog({
                    id: DialogComponents.DataCollectionDialog,
                    props: {
                        settings,
                        author,
                        coverUrl,
                    },
                })
            );
        } else {
            dispatch(dialogActions.hideDialog());
        }
    }, [
        voteId,
        dispatch,
        settings,
        author,
        channels,
        pollChannelId,
        dataCollectionRequired,
    ]);

    // Render the vote result message and associated animation
    const renderVoteResult = () => {
        if (guestUserVoted || authUserVoted) {
            return (
                <>
                    <p style={voteMessageStyle}>
                        Oh, it seems we got your vote already.
                    </p>
                    <p>Thank you, we are working on the results.</p>
                    <Lottie animationData={registeredVote} loop={2} />
                </>
            );
        }
        return (
            <>
                <p style={voteMessageStyle}>
                    Thank you! <br /> We got your vote.
                </p>
                <Lottie animationData={successfulVoted} loop={2} />
            </>
        );
    };
    // Handler: close the tab.
    const handleCloseTab = useCallback(() => {
        dispatch(dialogActions.hideDialog());
        navigate("/");
    }, [dispatch, navigate]);

    return (
        <Box
            sx={{
                textAlign: "center",
                minWidth: "250px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                mt: "50px",
            }}
        >
            {renderVoteResult()}
            {dataCollectionRequired || canViewResults ? (
                <Button
                    onClick={dispatchGuestDialog}
                    variant="contained"
                    color="primary"
                    sx={{
                        margin: "auto",
                        textAlign: "center",
                        display: "flex",
                        flexDirection: "column",
                        boxShadow: "none",
                    }}
                >
                    {ctaMsg}
                </Button>
            ) : (
                <Link
                    onClick={handleCloseTab}
                    component="button"
                    color="secondary"
                    sx={{
                        mt: "15px",
                        color: "#d32f2f",
                        textDecoration: "underline",
                        fontSize: "14px",
                    }}
                >
                    Close
                </Link>
            )}
        </Box>
    );
};

const VotedChoiceActions: React.FC = () => <></>;

export default {
    content: VotedChoiceContent,
    actions: VotedChoiceActions,
    title: "Voted Choice",
    disableCancelButton: true,
    isGuestData: false,
};
